<script setup>
    import { computed, provide, ref, nextTick } from 'vue';
    import { Form } from 'vee-validate';
    import { useI18n } from 'vue-i18n';
    import { useDebug } from '../../composables/useDebug';
    import { useCaptcha } from './composables/useCaptcha';
    import { useDccsForm } from './composables/useDccsForm';
    import { useAmsOsramForm } from './composables/useAmsOsramForm';
    import axios from '../../setup/axios';

    const { log, error } = useDebug();
    const { t } = useI18n();

    const props = defineProps({
        action: {
            type: String,
            required: true,
            default: null,
        },

        method: {
            type: String,
            default: 'post',
            validator: value => {
                return ['get', 'post', 'patch', 'delete'].indexOf(value) !== -1;
            },
        },

        buttonLabel: {
            type: String,
            default: 'Absenden',
        },

        buttonType: {
            type: String,
            default: 'is-primary',
            validator: value => ['is-primary', 'is-secondary', 'is-cream'].includes(value),
        },

        responseType: {
            type: String,
            default: 'json',
        },

        redirectUrl: {
            type: String,
            default: null,
        },

        submitSuccessMsg: {
            type: String,
            default: 'Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.',
        },

        submitErrorMsg: {
            type: String,
            default: 'Fehler! Ihre Nachricht konnte nicht gesendet werden:',
        },

        hideFormOnSuccess: {
            type: Boolean,
            default: true,
        },

        prefersOptional: {
            type: Boolean,
            default: false,
        },

        showSubmitButton: {
            type: String,
            default: 'true',
        },

        requiredAppend: {
            type: String,
            default: '*',
        },

        optionalAppend: {
            type: String,
            default: '(optional)',
        },

        formTitle: {
            type: String,
            default: null,
        },

        trackingTitle: {
            type: String,
            default: null,
        },

        urlValue: {
            type: String,
            default: null,
        },

        attachValue: {
            type: String,
            default: null,
        },

        appendFromUrl: {
            type: Boolean,
            default: false,
        },
    });

    // eslint-disable-next-line no-unused-vars
    const emit = defineEmits(['cancel']);

    const formRef = ref();
    const { captchaRef, captchaVerified, captchaSolution, reset: resetCaptcha } = useCaptcha(formRef);
    const { busy, generateFormPayload } = useDccsForm(props, captchaSolution);
    const { handleDataLayerFormTracking, generateAmsOsramPayload } = useAmsOsramForm(props);
    const sentSuccessfully = ref(false);
    const hasError = ref(false);
    const errorMessage = ref(null);
    const successMessageRef = ref(null);
    const errorMessageRef = ref(null);

    provide('busy', busy);
    provide('prefersOptional', props.prefersOptional);
    provide('requiredAppend', props.requiredAppend);
    provide('optionalAppend', props.optionalAppend);

    const submitForm = (rawFormData, { resetForm }) => {
        if (busy.value !== false) {
            return;
        }

        if (captchaVerified.value !== true) {
            hasError.value = true;
            errorMessage.value = t('form.error.captchaValidationError');
            return;
        }

        busy.value = true;
        hasError.value = false;
        errorMessage.value = null;

        log(rawFormData);

        return generateFormPayload(rawFormData)
            .then(payload => {
                log('Payload transformed successfully:', payload);

                handleDataLayerFormTracking(payload);
                const { payload: transformedPayload } = generateAmsOsramPayload(payload);

                axios[props.method](props.action, transformedPayload, { responseType: props.responseType })
                    .then(response => {
                        log('Form submitted successfully:', response);

                        sentSuccessfully.value = true;
                        resetCaptcha();
                        resetForm();

                        nextTick(() => {
                            successMessageRef.value?.scrollIntoView({ behavior: 'smooth' });
                        });
                    })
                    .catch(errorResponse => {
                        error(errorResponse);
                        hasError.value = true;

                        if (errorResponse.status === 403) {
                            errorMessage.value = 'Your session expired! Please refresh this page and try again.';
                        } else if (
                            errorResponse?.message?.length &&
                            errorResponse.message.includes('captcha invalid')
                        ) {
                            errorMessage.value = t('form.error.captchaValidationError');
                        } else if (
                            errorResponse?.response?.data?.length &&
                            errorResponse?.response?.data?.message?.length
                        ) {
                            errorMessage.value = errorResponse.response.data.message;
                        } else if (errorResponse?.message?.length) {
                            errorMessage.value = errorResponse.message;
                        } else {
                            errorMessage.value = 'Unknown error! Please try again later.';
                        }

                        nextTick(() => {
                            resetCaptcha();

                            if (errorMessageRef.value) {
                                errorMessageRef.value.scrollIntoView({ behavior: 'smooth' });
                            }
                        });
                    })
                    .finally(() => {
                        nextTick(() => {
                            busy.value = false;
                        });
                    });
            })
            .catch(errorResponse => {
                error(errorResponse);
                hasError.value = true;
                errorMessage.value = errorResponse.response?.message;
                resetCaptcha();

                nextTick(() => {
                    busy.value = false;
                });
            });
    };

    const isButtonDisabled = computed(() => busy.value || !captchaVerified.value);
    const buttonClasses = computed(() => ['button', props.buttonType, { 'is-loading': busy.value }]);

    /*export default {
        methods: {
            // eslint-disable-next-line no-unused-vars
            sendForm(passedData) {
                let data = passedData;
                let savedData = [];

                this.$http[this.method](this.action, data, config)
                    // eslint-disable-next-line no-unused-vars
                    .then(response => {

                    })
                    .catch(error => {
                        this.hasError = true;

                        if (error.status === 403) {
                            this.errorMessage = 'Your session expired! Please refresh this page and try again.';
                            return;
                        }

                        if (error !== undefined && error.response !== undefined && error.response.data !== undefined) {
                            this.errorMessage =
                                Object.prototype.hasOwnProperty.call(error, 'response') &&
                                Object.prototype.hasOwnProperty.call(error.response, 'data') &&
                                error.response.data &&
                                Object.prototype.hasOwnProperty.call(error.response.data, 'message')
                                    ? error.response.data.message
                                    : Object.prototype.hasOwnProperty.call(error, 'message')
                                        ? error.message
                                        : '';
                        } else {
                            this.errorMessage = '';
                        }

                        if (data.Subject !== undefined) {
                            this.$store.state.fields.Subject = data['Subject'].substring(
                                0,
                                data['Subject'].indexOf('|')
                            );
                        }

                        if (this.formTitle === 'SupportForm' && data.Description !== undefined) {
                            this.$store.state.fields.Description = data['Description'].substring(
                                0,
                                data['Description'].indexOf('|')
                            );

                            if (savedData.DocumentType__c !== null && savedData.DocumentType__c.length > 0) {
                                data['DocumentType__c'] = savedData['DocumentType__c'];
                                delete savedData['DocumentType__c'];
                            }
                            if (
                                savedData.DocumentVersion__c !== undefined &&
                                savedData.DocumentVersion__c !== null &&
                                savedData.DocumentVersion__c.length > 0
                            ) {
                                data['DocumentVersion__c'] = savedData['DocumentVersion__c'];
                                delete savedData['DocumentVersion__c'];
                            }
                            if (
                                savedData.DocumentVersion__c !== undefined &&
                                savedData.DocumentVersion__c !== null &&
                                savedData.DocumentVersion__c.length > 0
                            ) {
                                data['DocumentVersion__c'] = savedData['DocumentVersion__c'];
                                delete savedData['DocumentVersion__c'];
                            }
                            if (
                                savedData.DocumentNumber__c !== undefined &&
                                savedData.DocumentNumber__c !== null &&
                                savedData.DocumentNumber__c.length > 0
                            ) {
                                data['DocumentNumber__c'] = savedData['DocumentNumber__c'];
                                delete savedData['DocumentNumber__c'];
                            }
                            if (
                                savedData.DocumentTitle__c !== undefined &&
                                savedData.DocumentTitle__c !== null &&
                                savedData.DocumentTitle__c.length > 0
                            ) {
                                data['DocumentTitle__c'] = savedData['DocumentTitle__c'];
                                delete savedData['DocumentTitle__c'];
                            }
                            if (
                                savedData.PartNumber__c !== undefined &&
                                savedData.PartNumber__c !== null &&
                                savedData.PartNumber__c.length > 0
                            ) {
                                data['PartNumber__c'] = savedData['PartNumber__c'];
                                delete savedData['PartNumber__c'];
                            }
                            if (
                                savedData.ProductArea__c !== undefined &&
                                savedData.ProductArea__c !== null &&
                                savedData.ProductArea__c.length > 0
                            ) {
                                data['ProductArea__c'] = savedData['ProductArea__c'];
                                delete savedData['ProductArea__c'];
                            }
                            if (
                                savedData.ledTypes__c !== undefined &&
                                savedData.ledTypes__c !== null &&
                                savedData.ledTypes__c.length > 0
                            ) {
                                data['ledTypes__c'] = savedData['ledTypes__c'];
                                delete savedData['ledTypes__c'];
                            }
                            if (
                                savedData.laserType__c !== undefined &&
                                savedData.laserType__c !== null &&
                                savedData.laserType__c.length > 0
                            ) {
                                data['laserType__c'] = savedData['laserType__c'];
                                delete savedData['laserType__c'];
                            }
                            if (
                                savedData.photodetectorsType__c !== undefined &&
                                savedData.photodetectorsType__c !== null &&
                                savedData.photodetectorsType__c.length > 0
                            ) {
                                data['photodetectorsType__c'] = savedData['photodetectorsType__c'];
                                delete savedData['photodetectorsType__c'];
                            }
                            if (
                                savedData.sensorsType__c !== undefined &&
                                savedData.sensorsType__c !== null &&
                                savedData.sensorsType__c.length > 0
                            ) {
                                data['sensorsType__c'] = savedData['sensorsType__c'];
                                delete savedData['sensorsType__c'];
                            }
                            if (
                                savedData.interfacesType__c !== undefined &&
                                savedData.interfacesType__c !== null &&
                                savedData.interfacesType__c.length > 0
                            ) {
                                data['interfacesType__c'] = savedData['interfacesType__c'];
                                delete savedData['interfacesType__c'];
                            }
                        }

                        this.$nextTick(() => {
                            if (this.$refs.errorMessage) {
                                this.$refs.errorMessage.scrollIntoView({ behavior: 'smooth' });
                            }
                        });
                    })
                    .finally(() => {
                        this.busy = false;
                    });
            },
            submitForm(event) {
                if (this.busy) {
                    return;
                }
                this.busy = true;
                this.sentSuccessfully = false;
                this.hasError = false;
                this.errorMessage = null;

                this.$refs.validationObserver.validate().then(result => {
                    if (result !== true) {
                        this.busy = false;
                        return;
                    }

                    if (this.captchaSolution === null) {
                        this.errorMessage = 'Please click on the validation button and submit the form afterwards!';
                        this.hasError = true;
                        this.verify();

                        this.busy = false;
                        return;
                    } else if (this.captchaVerified !== true) {
                        this.errorMessage = 'Captcha Error!';
                        this.hasError = true;

                        this.busy = false;
                        return;
                    } else {
                        this.sendForm(this.$store.state.fields);
                    }
                });
            },
        },
    };*/
</script>

<template>
    <Form ref="formRef" v-slot="{ handleSubmit, isSubmitting }" as="div" class="dccs-form-container" role="form">
        <div v-if="!busy && !isSubmitting && hasError" ref="errorMessageRef" class="alert alert-danger">
            <span v-html="submitErrorMsg" />
            {{ errorMessage || 'Please try again later' }}
        </div>
        <div class="dccs-form-fields">
            <template v-if="!hideFormOnSuccess || (hideFormOnSuccess && !sentSuccessfully)">
                <slot />

                <slot name="beforeFormActions" />

                <div
                    :class="[
                        'dccs-form-actions',
                        'has-message',
                        {
                            'is-hidden': showSubmitButton === 'false',
                        },
                    ]"
                >
                    <div ref="captchaRef" class="dccs-form-captcha" />

                    <slot name="additionalFormActions" />
                    <button
                        :class="buttonClasses"
                        :disabled="isButtonDisabled || isSubmitting"
                        @click="event => handleSubmit(event, submitForm)"
                    >
                        {{ buttonLabel }}
                    </button>
                </div>
            </template>
        </div>

        <div ref="successMessageRef">
            <slot v-if="!busy && !isSubmitting && sentSuccessfully" name="successMessage">
                <div class="alert alert-success" v-html="submitSuccessMsg" />
            </slot>
        </div>
    </Form>
</template>

<style lang="scss">
    @import '../../../scss/variables';
    @import '../../../scss/utilities/all';
    @import '../../../scss/forms/_all.scss';
</style>
